import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { httpClient } from '@/utils/http/client'
import { trackEvent } from '@/utils/analytics'
import type {
  Plan,
  Subscription,
  Payment,
  BillingProviderConfig,
  BillingPortalResponse,
  SubscribeData,
  PlansResponse,
  SubscriptionResponse,
  SubscribeResponse,
  CancelSubscriptionResponse,
  ChangePlanData,
  ChangePlanResponse,
  PlanChangePreview,
  PaymentsResponse,
} from '@/types/billing'
import type { AxiosError } from 'axios'

export const useBillingStore = defineStore('billing', () => {
  // State
  const plans = ref<Plan[]>([])
  const subscription = ref<Subscription | null>(null)
  const payments = ref<Payment[]>([])
  const providerConfig = ref<BillingProviderConfig | null>(null)
  const tier = ref('free')
  const entitlements = ref<string[]>([])
  const tierOrder = ref<string[]>([])
  const entitlementsLoaded = ref(false)
  const entitlementsLoading = ref(false)
  const entitlementsError = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const accountGeneration = ref(0)
  let entitlementRequest: Promise<void> | null = null
  let entitlementGeneration = 0
  let activeEntitlementRequest = 0
  let accountEpoch = 0

  // Getters
  const hasActiveSubscription = computed(
    () => subscription.value?.status === 'active' || subscription.value?.status === 'trialing'
  )

  const currentPlan = computed(() => {
    if (!subscription.value) return null
    return plans.value.find((plan) => plan.id === subscription.value?.planId) || null
  })

  const isSubscriptionCanceled = computed(() => !!subscription.value?.canceledAt)
  const checkoutProvider = computed(() => {
    if (!providerConfig.value) return 'unknown'
    if (providerConfig.value?.provider === 'paddle') return 'paddle'
    if (providerConfig.value?.provider === 'fungies') return 'fungies'
    if (
      providerConfig.value.provider === 'mock' ||
      providerConfig.value.provider === 'stripe'
    ) {
      return 'legacy'
    }
    return 'unknown'
  })

  // Actions
  async function fetchPlans(): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.get<PlansResponse>('/billing/plans')
      plans.value = response.data.plans
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to fetch plans'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchBillingConfig(): Promise<void> {
    try {
      const response = await httpClient.get<BillingProviderConfig>('/billing/config')
      providerConfig.value = response.data
    } catch (err) {
      providerConfig.value = null
      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to fetch billing configuration'
      throw err
    }
  }

  async function fetchSubscription(force = false, preserveSubscription = false): Promise<void> {
    if (entitlementRequest) {
      if (!force) return entitlementRequest

      try {
        await entitlementRequest
      } catch {
        entitlementRequest = null
      }
    }

    const epoch = accountEpoch
    const generation = entitlementGeneration
    const requestId = ++activeEntitlementRequest
    loading.value = true
    entitlementsLoading.value = true
    entitlementsError.value = null
    error.value = null

    const request = (async () => {
      try {
        const response = await httpClient.get<SubscriptionResponse>('/billing/subscription')
        if (epoch !== accountEpoch || generation !== entitlementGeneration) return

        if (
          !preserveSubscription &&
          (response.data.subscription !== null || !subscription.value?.canceledAt)
        ) {
          subscription.value = response.data.subscription
        }
        tier.value = response.data.tier
        entitlements.value = response.data.entitlements
        tierOrder.value = response.data.tierOrder
        entitlementsLoaded.value = true
      } catch (err) {
        if (epoch !== accountEpoch) return

        if (generation === entitlementGeneration) {
          tier.value = 'free'
          entitlements.value = []
          tierOrder.value = []
          entitlementsLoaded.value = false
          const axiosError = err as AxiosError<{ message: string }>
          const message = axiosError.response?.data?.message || 'Failed to fetch subscription'
          entitlementsError.value = message
          error.value = message
        }
        throw err
      } finally {
        if (epoch === accountEpoch) {
          loading.value = false
        }
        if (epoch === accountEpoch && activeEntitlementRequest === requestId) {
          entitlementsLoading.value = false
          entitlementRequest = null
        }
      }
    })()
    entitlementRequest = request

    return request
  }

  async function subscribe(data: SubscribeData): Promise<void> {
    const epoch = accountEpoch
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.post<SubscribeResponse>('/billing/subscribe', data)
      if (epoch !== accountEpoch) return

      subscription.value = {
        id: response.data.id,
        planId: response.data.planId,
        status: response.data.status as Subscription['status'],
        currentPeriodStart: response.data.currentPeriodStart,
        currentPeriodEnd: response.data.currentPeriodEnd,
        createdAt: response.data.createdAt,
      }

      trackEvent('subscription_created', {
        planId: response.data.planId,
        status: response.data.status,
      })

      invalidateEntitlements()
      await refreshEntitlementsAfterMutation()
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to create subscription'
      throw err
    } finally {
      if (epoch === accountEpoch) {
        loading.value = false
      }
    }
  }

  async function cancelSubscription(): Promise<void> {
    const epoch = accountEpoch
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.patch<CancelSubscriptionResponse>(
        '/billing/subscription/cancel'
      )
      if (epoch !== accountEpoch) return

      subscription.value = response.data.subscription

      trackEvent('subscription_canceled', {
        planId: subscription.value?.planId,
      })

      invalidateEntitlements()
      await refreshEntitlementsAfterMutation(true)
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to cancel subscription'
      throw err
    } finally {
      if (epoch === accountEpoch) {
        loading.value = false
      }
    }
  }

  /**
   * Move the current subscription onto a different plan.
   *
   * The change applies immediately in both directions and is billed prorated -
   * the payment provider cannot defer a downgrade to the end of the period, it
   * can only defer the invoice.
   *
   * `subscription.value` is replaced from the RESPONSE, never from `planId`: a
   * declined proration charge leaves the subscription on its old plan, and
   * assuming otherwise would show the customer a plan they are not on. The
   * `currentPlan` getter resolves against the new planId, so nothing needs a
   * refetch.
   */
  async function changePlan(planId: string): Promise<void> {
    const epoch = accountEpoch
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.patch<ChangePlanResponse>('/billing/subscription/plan', {
        planId,
      } satisfies ChangePlanData)
      if (epoch !== accountEpoch) return

      subscription.value = response.data.subscription

      trackEvent('subscription_plan_changed', {
        planId: response.data.subscription.planId,
        status: response.data.subscription.status,
      })

      invalidateEntitlements()
      await refreshEntitlementsAfterMutation()
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to change plan'
      throw err
    } finally {
      if (epoch === accountEpoch) {
        loading.value = false
      }
    }
  }

  async function previewPlanChange(planId: string): Promise<PlanChangePreview | null> {
    const epoch = accountEpoch
    error.value = null

    try {
      const response = await httpClient.patch<PlanChangePreview>(
        '/billing/subscription/plan/preview',
        { planId } satisfies ChangePlanData
      )

      if (epoch !== accountEpoch) {
        throw new Error('Billing account changed')
      }

      return response.data
    } catch (err) {
      if (epoch !== accountEpoch) throw err

      const axiosError = err as AxiosError<{ error?: string; message: string }>
      if (
        axiosError.response?.status === 501 &&
        axiosError.response.data?.error === 'billing.payment_operation_unsupported'
      ) {
        return null
      }

      error.value = axiosError.response?.data?.message || 'Failed to preview plan change'
      throw err
    }
  }

  async function fetchPayments(): Promise<void> {
    const epoch = accountEpoch
    loading.value = true
    error.value = null

    try {
      const response = await httpClient.get<PaymentsResponse>('/billing/payments')
      if (epoch !== accountEpoch) return

      payments.value = response.data.payments
    } catch (err) {
      if (epoch !== accountEpoch) return

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to fetch payments'
      throw err
    } finally {
      if (epoch === accountEpoch) {
        loading.value = false
      }
    }
  }

  async function fetchBillingPortal(): Promise<string> {
    const epoch = accountEpoch
    error.value = null

    try {
      const response = await httpClient.get<BillingPortalResponse>('/billing/portal')
      if (epoch !== accountEpoch) {
        throw new Error('Billing account changed')
      }

      return response.data.url
    } catch (err) {
      if (epoch !== accountEpoch) throw err

      const axiosError = err as AxiosError<{ message: string }>
      error.value = axiosError.response?.data?.message || 'Failed to open billing portal'
      throw err
    }
  }

  function clearError() {
    error.value = null
  }

  function invalidateEntitlements(): void {
    entitlementGeneration++
    activeEntitlementRequest++
    entitlementRequest = null
    tier.value = 'free'
    entitlements.value = []
    tierOrder.value = []
    entitlementsLoaded.value = false
    entitlementsLoading.value = false
    entitlementsError.value = null
  }

  async function refreshEntitlementsAfterMutation(preserveSubscription = false): Promise<void> {
    try {
      await fetchSubscription(true, preserveSubscription)
    } catch {
      return
    }
  }

  function resetAccountState(): void {
    accountEpoch++
    accountGeneration.value++
    invalidateEntitlements()
    subscription.value = null
    payments.value = []
    loading.value = false
    error.value = null
  }

  /**
   * Get checkout URL for a plan (if available).
   * Returns null for plans without Fungies checkout configured.
   */
  function getCheckoutUrl(planId: string): string | null {
    const plan = plans.value.find((p) => p.id === planId)
    return plan?.checkoutUrl ?? null
  }

  /**
   * Check if a plan has Fungies checkout enabled.
   */
  function hasFungiesCheckout(planId: string): boolean {
    return getCheckoutUrl(planId) !== null
  }

  return {
    // State
    plans,
    subscription,
    payments,
    providerConfig,
    tier,
    entitlements,
    tierOrder,
    entitlementsLoaded,
    entitlementsLoading,
    entitlementsError,
    loading,
    error,
    accountGeneration,
    // Getters
    hasActiveSubscription,
    currentPlan,
    isSubscriptionCanceled,
    checkoutProvider,
    // Actions
    fetchPlans,
    fetchBillingConfig,
    fetchSubscription,
    subscribe,
    cancelSubscription,
    previewPlanChange,
    changePlan,
    fetchPayments,
    fetchBillingPortal,
    clearError,
    invalidateEntitlements,
    resetAccountState,
    getCheckoutUrl,
    hasFungiesCheckout,
  }
})
